import React, {useEffect, useState} from 'react';

import * as api from '../../lib/api';
import {useCredentials} from '../../lib/user';
import Form from '../../components/Form';
import Notification from '../../components/Notification';
import {receivesSharedAnswers} from '../FormBuilderScreen/shared';

// Shown while editing an answer to a question that receives shared answers. Editing a submitted answer never changes
// which One-time use answers are offered, so a question with a One-time use source warns about that; one with only
// Reusable sources just notes where its shared answers come from. Nothing is shown until the linked sources have
// loaded, or if they can't be.
function SharedAnswerNotice({question, currentValue}) {
  const credentials = useCredentials();
  const receives = receivesSharedAnswers(question);
  const [sources, setSources] = useState(null);

  useEffect(() => {
    if (!receives) {
      return;
    }

    let cancelled = false;
    api.getReceivingQuestionLinks(question.id, credentials)
      .then(links => {
        if (!cancelled) {
          setSources(links);
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [receives, question.id, credentials]);

  if (!receives || !sources || sources.length === 0) {
    return null;
  }

  const forms = formatList([...new Set(sources.map(source => source.sourceTemplateName).filter(Boolean))]);
  const from = forms ? ` from ${forms}` : '';
  const oneTime = sources.filter(source => source.reuseMode === 'one_time').length;

  if (oneTime === 0) {
    return (
      <Form.Group>
        <Notification info mini>
          This question receives shared answers{from}. Its linked sources are Reusable, so changing this answer doesn't
          change what's offered on other submissions.
        </Notification>
      </Form.Group>
    );
  }

  const which = oneTime === sources.length ? 'Its linked sources are' : 'Some of its linked sources are';
  const current = currentValue ? <strong>{currentValue}</strong> : 'the current answer';

  return (
    <Form.Group>
      <Notification warning mini>
        This question receives shared answers{from}. {which} One-time use, and changing an answer here doesn't change
        what's offered. If {current} was a One-time use answer, it still won't be offered again, and a One-time use answer you
        pick here is still offered on other submissions.
      </Notification>
    </Form.Group>
  );
}

function formatList(items) {
  if (items.length <= 1) {
    return items[0] || '';
  }
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export default SharedAnswerNotice;
